import { useQueryClient, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useState } from 'react';
import { ApiException } from '../../../../../../../types';
import { useAnalytics } from '../../../../../../shared/analytics/create-events';
import { ModuleList } from '../../../../../../shared/analytics/module-list';
import ChangeLogDialog from '../../../../../../shared/change-log-dialog';
import { handleErrors } from '../../../../../../utils/errors';
import {
  GPanelContactsPermissions,
  hasPermission,
} from '../../../../../../utils/roles/client-roles';
import { useUserSettings } from '../../../../../../utils/use-user-settings';
import {
  GPanelContactsKeys,
  restoreContacts,
} from '../../../gpanel-contacts-api';
import { useStore } from '../../../../../state/zustand-store';
import * as analytics from '../../../../../../shared/analytics/analytics.json';
import { Messages } from '../../../../../../utils/message-constants';
import SharedDialog from '../../../../../../shared/shared-dialog';
import { Contact } from '../../../shared/types';

import Typography from '@mui/material/Typography';
import FormHelperText from '@mui/material/FormHelperText';

export default function GpanelContactsRecoverContactsDialog({
  handleClose,
  contactsIds,
}: {
  handleClose: () => void;
  contactsIds: number[];
}) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const setSnackbar = useStore((state) => state.setSnackbar);
  const [errorText, setErrorText] = useState<string | null>(null);
  const { changeLogRoles } = useUserSettings();

  const trashedContacts =
    queryClient.getQueryData<Contact[]>([GPanelContactsKeys.TrashContacts]) ??
    [];

  const selectedContact =
    contactsIds.length === 1
      ? trashedContacts.find((c) => Number(c.id) === contactsIds[0])
      : null;

  const contactName = selectedContact
    ? [selectedContact.firstName, selectedContact.lastName]
        .filter(Boolean)
        .join(' ')
    : null;

  const { mutate, isPending } = useMutation({
    mutationFn: () => restoreContacts(contactsIds, changeLogMessage),
    onSuccess: () => {
      setChangeLogMessage('');
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Restored(
          `Contact${contactsIds.length > 1 ? 's' : ''}`
        ),
      });
      queryClient.invalidateQueries({
        queryKey: [GPanelContactsKeys.TrashContacts],
      });
      queryClient.invalidateQueries({
        queryKey: [GPanelContactsKeys.LabelGroups],
      });
      queryClient.invalidateQueries({
        queryKey: [GPanelContactsKeys.OrganizationDirectoryContacts],
      });
      handleClose();
    },
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        const errorResponse = error.response?.data as ApiException;
        if (errorResponse.message) {
          setErrorText(errorResponse.message);
        } else {
          handleErrors({
            error,
            snackbarMessage: Messages.Errors.Network.Restoring(`contacts`),
          });
        }
      }
    },
    onSettled: () => {
      createEvent(
        analytics.directory.gpanel_contacts.restore_from_contact_trash,
        {
          module: ModuleList.modules.directory.name,
          section: ModuleList.modules.directory.sections.gpanelContacts,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      hasPermission(
        changeLogRoles,
        GPanelContactsPermissions.RecoverTrashedContacts.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  const contactSuffix = contactsIds.length > 1 ? 's' : '';

  return (
    <>
      <SharedDialog
        open
        handleClose={handleClose}
        title={`Restore Contact${contactSuffix}`}
        dialogContent={
          <>
            <Typography>
              {contactsIds.length > 1 ? (
                <>
                  Restore <strong>{contactsIds.length}</strong> and associated
                  data.
                </>
              ) : (
                <>
                  Restore <strong>{contactName ?? 'the contact'}</strong> and
                  associated data.
                </>
              )}
            </Typography>
            {errorText && <FormHelperText error children={errorText} />}
          </>
        }
        primaryButton={{
          title: `Restore Contact${contactSuffix}`,
          color: 'primary',
          action: handleSubmit,
          loading: isPending,
        }}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={mutate}
        mutateLoading={isPending}
      />
    </>
  );
}
